import utils from "../utils";
import { BaseApi, ApiResponse } from "../utils/request";

function getDepositTimezoneHeaders(): Record<string, string> {
  try {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (timeZone) {
      return { tzz: timeZone };
    }
  } catch {
    /* ignore */
  }
  return {};
}

// 充值相关类型定义
export interface RechargeRequest {
  amount: string;
  currencyId: string;
  actionSource: {
    url: string;
  };
  disablePromotion: boolean;
  rechargeType: string;
  payMethod: string;
  usedDailyRechargePromo: boolean;
  payTag?: string;
  subCurrency?: boolean;
  activityId?: string;
  /** 分批领取加倍：目标批次 stageId（格式 {首充rechargeRecordId}:{targetIndex}） */
  stagedClaimTargetId?: string;
  /** VIP 反水充值翻倍任务 ID */
  cashbackBoostRecordId?: string;
}

export interface RechargeResponse {
  success: boolean;
  message?: string;
  model?: {
    currencyAmount: number;
    currencyId: string;
    id: string;
    payCode: string;
    payMode: string;
    orderId?: string;
    paymentUrl?: string;
    qrCode?: string;
    transactionId?: string;
  };
  // 保留这些字段用于向后兼容和附加信息
  orderId?: string;
  paymentUrl?: string;
  qrCode?: string;
  transactionId?: string;
  payMode?: string;
  payCode?: string;
  id?: string;
  symbol?: string;
  amount?: string;
  extra?: number;
}

export interface PaymentMethod {
  id: string;
  name: string;
  icon?: string;
  enabled: boolean;
}

export interface Currency {
  id: string;
  name: string;
  symbol: string;
  rate: number;
}

export interface RechargeHistoryItem {
  id: string;
  amount: string;
  currency: string;
  status: "pending" | "success" | "failed" | "cancelled";
  createdAt: string;
  completedAt?: string;
  transactionId?: string;
}

/**
 * 支付 API 类
 * 继承 BaseApi，提供充值相关的 API 接口
 */
export class PayApi extends BaseApi {
  constructor() {
    super();
  }

  /**
   * 创建充值订单
   * @param data 充值请求数据
   * @returns Promise<ApiResponse<RechargeResponse>>
   */
  async recharge(
    data: RechargeRequest
  ): Promise<ApiResponse<RechargeResponse>> {
    return this.post<RechargeResponse>(
      "/api/pay/recharge",
      {
        ...data,
        countryId: utils.getObject("countryId"),
      },
      { headers: getDepositTimezoneHeaders() }
    );
  }

  /**
   * 获取可用的支付方式
   * @returns Promise<ApiResponse<PaymentMethod[]>>
   */
  async getPaymentMethods(): Promise<ApiResponse<PaymentMethod[]>> {
    return this.get<PaymentMethod[]>("/api/pay/methods");
  }

  /**
   * 获取支持的货币列表
   * @returns Promise<ApiResponse<Currency[]>>
   */
  async getCurrencies(): Promise<ApiResponse<Currency[]>> {
    return this.get<Currency[]>("/api/pay/currencies");
  }

  /**
   * 查询充值订单状态
   * @param orderId 订单ID
   * @returns Promise<ApiResponse<RechargeResponse>>
   */
  async getOrderStatus(
    orderId: string
  ): Promise<ApiResponse<RechargeResponse>> {
    return this.get<RechargeResponse>(`/api/pay/order/${orderId}`);
  }

  /**
   * 获取充值历史记录
   * @param page 页码
   * @param limit 每页数量
   * @returns Promise<ApiResponse<{ items: RechargeHistoryItem[]; total: number; page: number; limit: number }>>
   */
  async getRechargeHistory(
    page: number = 1,
    limit: number = 20
  ): Promise<
    ApiResponse<{
      items: RechargeHistoryItem[];
      total: number;
      page: number;
      limit: number;
    }>
  > {
    return this.get<{
      items: RechargeHistoryItem[];
      total: number;
      page: number;
      limit: number;
    }>(`/api/pay/history?page=${page}&limit=${limit}`);
  }

  /**
   * 取消充值订单
   * @param orderId 订单ID
   * @returns Promise<ApiResponse<{ success: boolean; message?: string }>>
   */
  async cancelOrder(
    orderId: string
  ): Promise<ApiResponse<{ success: boolean; message?: string }>> {
    return this.post<{ success: boolean; message?: string }>(
      `/api/pay/cancel/${orderId}`
    );
  }

  /**
   * 获取提现配置
   * @param data 可选 currencyId（子货币）、countryId（未传则用本地选中国家，便于美国等分地区字段）
   */
  async getWithdrawConfig(
    data: { currencyId?: string; countryId?: string } = {}
  ): Promise<ApiResponse<any, true>> {
    const payload: { currencyId?: string; countryId?: string } = { ...data };
    const cid = utils.getObject("countryId");
    if (cid && payload.countryId === undefined) {
      payload.countryId = cid as string;
    }
    return this.post<any, true>("/api/pay/withdraw_config", payload);
  }

  /**
   * 获取银行列表
   */
  async getBankList(
    data: { countryId?: string } = {}
  ): Promise<ApiResponse<any, true>> {
    const payload: { countryId?: string } = { ...data };
    const cid = utils.getObject("countryId");
    if (cid && payload.countryId === undefined) {
      payload.countryId = cid as string;
    }
    return this.post<any, true>("/api/pay/bank_list", payload);
  }

  /**
   * 绑定提现卡信息
   * @param data 绑卡数据
   * @returns Promise<ApiResponse<any, true>>
   */
  async persistCard(data: any): Promise<ApiResponse<any, true>> {
    return this.post<any, true>("/api/pay/persist_card", data);
  }

  /**
   * 智利提款：专用身份证正反面
   */
  async uploadChileWithdrawId(data: {
    idFrontImageId: string;
    idBackImageId: string;
  }): Promise<ApiResponse<any, true>> {
    return this.post<any, true>("/api/pay/upload_chile_withdraw_id", data, {
      attachApiExtraOnBizError: true,
    });
  }

  /**
   * ZAR 代金券建单前预检：voucherNumber 在系统中是否已被其它订单使用。
   * 仅 ZAR 凭证类 payMethod 需要调用；后端若重复则抛 VOUCHER_CODE_ALREADY_USED 错误。
   */
  async checkZarVoucher(
    payMethod: string,
    voucherNumber: string
  ): Promise<ApiResponse<any, true>> {
    return this.post<any, true>(
      "/api/pay/check_zar_voucher",
      { payMethod, voucherNumber },
      {
        attachApiExtraOnBizError: true,
      }
    );
  }

  /**
   * 提现前 KYC 引导（西班牙等国家由后端决定是否弹窗）
   */
  async getWithdrawKycGuide(): Promise<ApiResponse<any, true>> {
    return this.get<any, true>("/api/pay/withdraw_kyc_guide", {}, {
      alert: false,
    });
  }

  /**
   * @param data 提现请求数据
   * @returns Promise<ApiResponse<any, true>>
   */
  async withdraw(data: {
    currencyId: string;
    amount: number;
    subCurrency?: boolean;
  }): Promise<ApiResponse<any, true>> {
    return this.post<any, true>("/api/pay/withdraw", data);
  }

  /**
   * 7天充值活动充值
   * @param dateStr 日期字符串 yyyyMMdd
   * @param data 充值请求数据
   * @returns Promise<ApiResponse<RechargeResponse>>
   */
  async dailyRecharge(
    dateStr: string,
    data: RechargeRequest
  ): Promise<ApiResponse<RechargeResponse>> {
    return this.post<RechargeResponse>(
      `/api/daily/recharge/${dateStr}`,
      {
        ...data,
        countryId: utils.getObject("countryId"),
      },
      { headers: getDepositTimezoneHeaders() }
    );
  }
}

export const payApi = new PayApi();

// 便捷方法
export const createRechargeOrder = (data: RechargeRequest) =>
  payApi.recharge(data);

export const getPaymentMethods = () => payApi.getPaymentMethods();

export const getCurrencies = () => payApi.getCurrencies();

export const getOrderStatus = (orderId: string) =>
  payApi.getOrderStatus(orderId);

export const getRechargeHistory = (page?: number, limit?: number) =>
  payApi.getRechargeHistory(page, limit);

export const cancelOrder = (orderId: string) => payApi.cancelOrder(orderId);

export const getWithdrawConfig = (data?: {
  currencyId?: string;
  countryId?: string;
}) => payApi.getWithdrawConfig(data ?? {});

export const getBankList = (data?: { countryId?: string }) =>
  payApi.getBankList(data ?? {});

export const persistCard = (data: any) => payApi.persistCard(data);

export const uploadChileWithdrawId = (data: {
  idFrontImageId: string;
  idBackImageId: string;
}) => payApi.uploadChileWithdrawId(data);

export const checkZarVoucher = (payMethod: string, voucherNumber: string) =>
  payApi.checkZarVoucher(payMethod, voucherNumber);

export interface WithdrawKycGuideModel {
  guideEnabled?: boolean;
  enabled?: boolean;
  needGuide?: boolean;
  callbackTitle?: string;
  callbackContent?: string;
  title?: string;
  content?: string;
}

export function resolveWithdrawKycGuide(
  model?: WithdrawKycGuideModel | null
): { enabled: boolean; title: string; content: string } {
  if (!model) {
    return { enabled: false, title: "", content: "" };
  }
  const enabled =
    model.guideEnabled ?? model.enabled ?? model.needGuide ?? false;
  const title = model.callbackTitle ?? model.title ?? "";
  const content = model.callbackContent ?? model.content ?? "";
  return { enabled: !!enabled, title, content };
}

export const getWithdrawKycGuide = () => payApi.getWithdrawKycGuide();

export const withdraw = (data: {
  currencyId: string;
  amount: number;
  subCurrency?: boolean;
}) => payApi.withdraw(data);

export const dailyRecharge = (dateStr: string, data: RechargeRequest) =>
  payApi.dailyRecharge(dateStr, data);
